import React from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Card,
  Grid,
  Alert,
  Button,
  SvgIcon,
  AlertTitle,
  Typography,
  CardHeader,
  InputLabel,
  CardContent,
  CardActions,
} from '@mui/material';

import {
  Loading,
  Asterisk,
  TextField,
  SnakebarToaster,
  SimpleRadioGroupField,
  AutoCompleteSingleSelect,
} from '@/components';
import { CROSSING_DIRECTION_COUNTRY } from '@/features/shared/constants';
import {
  isBorderCrossingRoRo,
  OPEN_CUSTOMS_AND_CROSSING,
  isBorderCrossingRoRoEmptyV2,
} from '@/features/shared/helpers/typologies';
import { useCountriesOptions } from '@/features/shared/hooks';
import { OrangeClockIcon, SendPlaneLineIcon } from '@/utils/iconSVG';

import {
  stylesCreateNewRequestCard,
  stylesAutoCompleteSingleSelect,
  stylesCreateNewRequestCardContent,
  stylesCreateNewRequestFormSubtitle,
  stylesCreateNewRequestCardSubheader,
} from './CreateNewRequest.styles';
import { useCreateNewRequest } from './hooks';
import { REGISTER_NEW_REQUEST_FORM } from './types';

export const CreateNewRequest: React.FunctionComponent = () => {
  const { t } = useTranslation('borderCrossingRORO_newRequest');
  const { countriesOptions, isCountriesReferentialLoading } = useCountriesOptions();

  const {
    isCreateNewBorderCrossingRoroRequestPending,
    isGmrCustomsProFeatureFlagLoading,
    isGmrEloOptionActivatedEnabled,
    isCreateNewRequestInError,
    formState: { errors },
    control,
    register,
    handleCloseToaster,
    handleCancelTheRequest,
    handleSubmitNewRequest,
  } = useCreateNewRequest();

  const { requiresGmrAndEloFormality, folderTypologyId, direction, vehicleNationality } =
    useWatch({ control }) as {
      requiresGmrAndEloFormality: string;
      vehicleNationality: string;
      folderTypologyId: string;
      direction: string;
    };

  if (isGmrCustomsProFeatureFlagLoading || isCountriesReferentialLoading) {
    return <Loading />;
  }

  const requiresGmrAndEloFormalityValue = JSON.parse(requiresGmrAndEloFormality);

  return (
    <>
      <Card sx={stylesCreateNewRequestCard}>
        <CardHeader
          title={
            <Typography variant='h1' color='primary.dark2'>
              {t('borderCrossingRORO.newRequest.form.label')}
            </Typography>
          }
          subheader={
            <Typography variant='h2' color='primary.dark2' sx={stylesCreateNewRequestCardSubheader}>
              {t('borderCrossingRORO.newRequest.form.customsProENS.label')}
            </Typography>
          }
          disableTypography
        />
        <CardContent sx={stylesCreateNewRequestCardContent}>
          <Typography component='div' variant='bodyMDMedium' sx={stylesCreateNewRequestFormSubtitle}>
            {t('borderCrossingRORO.newRequest.form.generalInformation.label')}
          </Typography>
          <Grid container spacing={2} item xs={12} sm={12} md={12} lg={6} xl={6}>
            <Grid item xs={12}>
              <SimpleRadioGroupField
                isRequired
                control={control}
                labelSx={{ pb: 1 }}
                error={errors.direction}
                sx={{ width: '100%', mb: 0, zIndex: 1 }}
                id={REGISTER_NEW_REQUEST_FORM.direction}
                name={REGISTER_NEW_REQUEST_FORM.direction}
                title={t('borderCrossingRORO.newRequest.form.direction.label')}
                options={[
                  {
                    label: (
                      <Box component='span' sx={{ fontWeight: 'bold' }}>
                        {t('borderCrossingRORO.newRequest.form.EuToGb.label')}
                      </Box>
                    ),
                    value: CROSSING_DIRECTION_COUNTRY.EUUK,
                  },
                  {
                    label: (
                      <Box component='span' sx={{ fontWeight: 'bold' }}>
                        {t('borderCrossingRORO.newRequest.form.GbToEu.label')}
                      </Box>
                    ),
                    value: CROSSING_DIRECTION_COUNTRY.UKEU,
                  },
                ]}
              />
            </Grid>
            <Grid item xs={12}>
              <SimpleRadioGroupField
                isRequired
                control={control}
                labelSx={{ pb: 1 }}
                error={errors.folderTypologyId}
                sx={{ width: '100%', mb: 0, zIndex: 1 }}
                id={REGISTER_NEW_REQUEST_FORM.folderTypologyId}
                name={REGISTER_NEW_REQUEST_FORM.folderTypologyId}
                title={t('borderCrossingRORO.newRequest.form.trailerLoadMode.label')}
                options={[
                  {
                    label: (
                      <Box component='span' sx={{ fontWeight: 'bold' }}>
                        {t('borderCrossingRORO.newRequest.form.trailerLoadMode.full.label')}
                      </Box>
                    ),
                    value: OPEN_CUSTOMS_AND_CROSSING.BORDER_CROSSING_RORO,
                  },
                  {
                    label: (
                      <Box component='span' sx={{ fontWeight: 'bold' }}>
                        {t('borderCrossingRORO.newRequest.form.trailerLoadMode.empty.label')}
                      </Box>
                    ),
                    value: OPEN_CUSTOMS_AND_CROSSING.BORDER_CROSSING_RORO_EMPTY_V2,
                  },
                ]}
              />
            </Grid>

            {direction && isBorderCrossingRoRo({ folderTypologyId }) && (
              <Grid item xs={12}>
                <Grid container spacing={2} item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Typography
                      component='div'
                      variant='bodyMDMedium'
                      sx={{ ...stylesCreateNewRequestFormSubtitle, pb: 0 }}
                    >
                      {t('borderCrossingRORO.newRequest.form.transportInformation.label')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    {direction === CROSSING_DIRECTION_COUNTRY.EUUK ? (
                      <TextField
                        isRequired
                        labelSx={{ pb: 1 }}
                        error={errors.haulierGbEoriNumber}
                        id={REGISTER_NEW_REQUEST_FORM.haulierGbEoriNumber}
                        registration={register(REGISTER_NEW_REQUEST_FORM.haulierGbEoriNumber)}
                        label={t('borderCrossingRORO.newRequest.form.haulierGbEoriNumber.label')}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\s+/g, '').replace('-', '').replace('_', '');
                          e.target.value = value.toUpperCase();
                          register(REGISTER_NEW_REQUEST_FORM.haulierGbEoriNumber).onChange(e);
                        }}
                      />
                    ) : (
                      <TextField
                        isRequired
                        labelSx={{ pb: 1 }}
                        error={errors.haulierEuEoriNumber}
                        id={REGISTER_NEW_REQUEST_FORM.haulierEuEoriNumber}
                        registration={register(REGISTER_NEW_REQUEST_FORM.haulierEuEoriNumber)}
                        label={t('borderCrossingRORO.newRequest.form.haulierEuEoriNumber.label')}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\s+/g, '').replace('-', '').replace('_', '');
                          e.target.value = value.toUpperCase();
                          register(REGISTER_NEW_REQUEST_FORM.haulierEuEoriNumber).onChange(e);
                        }}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography component='div' variant='bodyMDMedium' sx={stylesCreateNewRequestFormSubtitle}>
                {t(`borderCrossingRORO.newRequest.form.additionalInformation.available.label`)}
              </Typography>
              <SimpleRadioGroupField
                isRequired={isBorderCrossingRoRoEmptyV2({ folderTypologyId })}
                control={control}
                labelSx={{ pb: 1 }}
                error={errors.requiresGmrAndEloFormality}
                sx={{ width: '100%', mb: 0, zIndex: 1 }}
                disabled={!isGmrEloOptionActivatedEnabled}
                id={REGISTER_NEW_REQUEST_FORM.requiresGmrAndEloFormality}
                name={REGISTER_NEW_REQUEST_FORM.requiresGmrAndEloFormality}
                title={t('borderCrossingRORO.newRequest.form.gmr.label')}
                options={[
                  {
                    label: (
                      <Box component='span' sx={{ fontWeight: 'bold' }}>
                        {t('borderCrossingRORO.newRequest.form.gmr.no.label')}
                      </Box>
                    ),
                    value: false,
                  },
                  {
                    label: (
                      <Box component='span' sx={{ fontWeight: 'bold' }}>
                        {t('borderCrossingRORO.newRequest.form.gmr.yes.label')}
                      </Box>
                    ),
                    value: true,
                  },
                ]}
              />
            </Grid>
            <Grid item xs={12}>
              {isBorderCrossingRoRoEmptyV2({ folderTypologyId }) && requiresGmrAndEloFormalityValue === false && (
                <Alert severity='warning' icon={<SvgIcon component={OrangeClockIcon} />}>
                  <AlertTitle> {t('borderCrossingRORO.newRequest.gmr.warning.title')}</AlertTitle>
                  <Box component='span' color='grey.800'>
                    {t('borderCrossingRORO.newRequest.requiresGmrAndEloFormality.whenNotSelected.warning.content')}
                  </Box>
                </Alert>
              )}
              {requiresGmrAndEloFormalityValue && (
                <Alert severity='warning' icon={<SvgIcon component={OrangeClockIcon} />}>
                  <AlertTitle> {t('borderCrossingRORO.newRequest.gmr.warning.title')}</AlertTitle>
                  <Box component='span' color='grey.800'>
                    {t('borderCrossingRORO.newRequest.requiresGmrAndEloFormality.whenSelected.warning.content')}
                  </Box>
                  <br />
                  {isBorderCrossingRoRo({ folderTypologyId }) && (
                    <Box component='span' color='grey.800'>
                      {t(
                        'borderCrossingRORO.newRequest.requiresGmrAndEloFormality.whenSelected.with.full.trailerMode.warning.content',
                      )}
                    </Box>
                  )}
                </Alert>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                labelSx={{ pb: 1 }}
                sx={{ mb: 0 }}
                error={errors.internalClientReference}
                id={REGISTER_NEW_REQUEST_FORM.internalClientReference}
                registration={register(REGISTER_NEW_REQUEST_FORM.internalClientReference)}
                label={t('borderCrossingRORO.newRequest.form.internalClientReference.label')}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel sx={{ mb: 1 }} htmlFor='tractor-registration'>
                {t('borderCrossingRORO.newRequest.form.tractorVrn.label')}
                <Asterisk />
              </InputLabel>
              <TextField
                fullWidth
                sx={{ mb: 0 }}
                variant='outlined'
                placeholder='Ex : NH132PQ'
                error={errors.tractorVrn}
                id={REGISTER_NEW_REQUEST_FORM.tractorVrn}
                inputProps={{ sx: { textTransform: 'uppercase' } }}
                registration={register(REGISTER_NEW_REQUEST_FORM.tractorVrn)}
                onChange={(e) => {
                  const value = e.target.value.replace(/\s+/g, '').replace('-', '').replace('_', '');
                  e.target.value = value.toUpperCase();
                  register(REGISTER_NEW_REQUEST_FORM.tractorVrn).onChange(e);
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel sx={{ mb: 1 }} htmlFor='trailer-registration'>
                {t('borderCrossingRORO.newRequest.form.trailerVrn.label')}
                <Asterisk />
              </InputLabel>
              <TextField
                fullWidth
                sx={{ mb: 0 }}
                id={REGISTER_NEW_REQUEST_FORM.trailerVrn}
                variant='outlined'
                placeholder='Ex : PL132WO'
                registration={register(REGISTER_NEW_REQUEST_FORM.trailerVrn)}
                error={errors.trailerVrn}
                inputProps={{
                  sx: {
                    textTransform: 'uppercase',
                  },
                }}
                onChange={(e) => {
                  const value = e.target.value.replace(/\s+/g, '').replace('-', '').replace('_', '');
                  e.target.value = value.toUpperCase();
                  register(REGISTER_NEW_REQUEST_FORM.trailerVrn).onChange(e);
                }}
              />
            </Grid>
            {isBorderCrossingRoRo({ folderTypologyId }) && (
              <Grid item xs={6}>
                <Controller
                  control={control}
                  key={vehicleNationality}
                  name={REGISTER_NEW_REQUEST_FORM.vehicleNationality}
                  render={({ field: { value: prevValue, onChange } }) => (
                    <AutoCompleteSingleSelect
                      isRequired
                      multiple={false}
                      labelSx={{ pb: 1 }}
                      options={countriesOptions}
                      error={errors.vehicleNationality}
                      sx={stylesAutoCompleteSingleSelect}
                      getOptionLabel={(option) => option.label}
                      value={countriesOptions.find((item) => item.value === prevValue) ?? null}
                      label={t('borderCrossingRORO.newRequest.form.vehicleNationality.label')}
                      onChange={(_, data) => {
                        onChange?.(data?.value);
                      }}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <InputLabel sx={{ mb: 1 }} htmlFor='crossing-requested-date'>
                {t('borderCrossingRORO.newRequest.form.crossingRequestedDate.label')}
                <Asterisk />
              </InputLabel>
              <TextField
                fullWidth
                type='date'
                variant='outlined'
                id={REGISTER_NEW_REQUEST_FORM.crossingRequestedDate}
                registration={register(REGISTER_NEW_REQUEST_FORM.crossingRequestedDate)}
                inputProps={{
                  min: new Date().toISOString().split('T')[0],
                  max: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
                }}
                error={errors.crossingRequestedDate}
              />
            </Grid>
          </Grid>
        </CardContent>
        <CardActions sx={{ p: 3 }}>
          <Grid container spacing={2} item xs={12} sm={12} md={12} lg={126} xl={6}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant='contained' color='inherit' sx={{ mr: 'auto' }} onClick={handleCancelTheRequest}>
                {t('borderCrossingRORO.newRequest.form.cancelButton.label')}
              </Button>
              <Button
                startIcon={<SendPlaneLineIcon />}
                color='success'
                variant='contained'
                disabled={
                  (isBorderCrossingRoRoEmptyV2({ folderTypologyId }) && !requiresGmrAndEloFormalityValue) ||
                  isCreateNewBorderCrossingRoroRequestPending
                }
                onClick={handleSubmitNewRequest}
              >
                {t('borderCrossingRORO.newRequest.form.validateAndSend.label')}
              </Button>
            </Grid>
          </Grid>
        </CardActions>
      </Card>

      {isCreateNewRequestInError && (
        <SnakebarToaster
          isOpen
          severity='error'
          autoHideDuration={null}
          onClose={handleCloseToaster}
          content={t('borderCrossingRORO.newRequest.error.toasterMessage', {
            supportEmail: process.env.REACT_APP_SUPPORT_EMAIL,
          })}
        />
      )}
    </>
  );
};
